/**
 * Returns the left safe-area inset in logical pixels (e.g. Dynamic Island on iPhone 16).
 * Returns 0 on devices/browsers with no inset, or when viewport-fit=cover is not set.
 */
export function getSafeAreaInsetLeft(): number {
  const el = document.createElement("div");
  el.style.cssText = "position:fixed;padding-left:env(safe-area-inset-left)";
  document.body.appendChild(el);
  const inset = parseFloat(getComputedStyle(el).paddingLeft) || 0;
  document.body.removeChild(el);
  return inset;
}

/**
 * Returns the right safe-area inset in logical pixels (e.g. the rounded corner/home
 * indicator edge on a landscape iPhone). Returns 0 on devices/browsers with no inset.
 */
export function getSafeAreaInsetRight(): number {
  const el = document.createElement("div");
  el.style.cssText = "position:fixed;padding-right:env(safe-area-inset-right)";
  document.body.appendChild(el);
  const inset = parseFloat(getComputedStyle(el).paddingRight) || 0;
  document.body.removeChild(el);
  return inset;
}

/**
 * Detects if the current device is a tablet based on the user agent string and screen size.
 * @returns true if the device is a tablet, false otherwise
 */
export function isTablet(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTabletDevice =
    /ipad|android(?!.*mobile)|(android.*tablet)|kindle|playbook|silk|(windows.*touch)/.test(
      userAgent
    );

  // Also check screen dimensions for touch devices
  const screenDiagonal = Math.sqrt(
    window.innerWidth ** 2 + window.innerHeight ** 2
  );
  const isLargeScreen = screenDiagonal > 1000;
  const hasNoHover = window.matchMedia("(hover: none)").matches;

  return isTabletDevice || (isLargeScreen && hasNoHover);
}

/**
 * Detects if the current device is a mobile device (phone) based on the user agent string.
 * @returns true if the device is a phone, false otherwise
 */
export function isMobile(): boolean {
  return /Mobi|iPhone|iPod|Opera Mini|IEMobile|WPDesktop|webOS|BlackBerry/i.test(
    navigator.userAgent
  );
}

/**
 * Detects if the current device is a touch device (mobile phone or tablet).
 * @returns true if the device is a phone or tablet, false otherwise
 */
export function isTouchDevice(): boolean {
  const mobile = isMobile();
  const tablet = isTablet();
  const touchDevice = mobile || tablet;

  return touchDevice;
}
